import React, { useState, useEffect } from 'react';
import ProductCard, { ProductProps } from './ProductCard';
import { productsAPI, categoriesAPI } from '../../services/api';

type SortOption = 'default' | 'price-low' | 'price-high';

// Adapter function to ensure API data matches ProductProps interface
const adaptProductData = (apiProduct: any): ProductProps => {
  // Handle image URL
  let imageUrl = apiProduct.image || 'https://via.placeholder.com/300?text=No+Image';
  
  // Log the image URL to help with debugging
  console.log('Processing product image URL:', imageUrl);
  
  return {
    id: apiProduct.id || String(Date.now()),
    name: apiProduct.name || 'Unnamed Product',
    productCode: apiProduct.productCode || 'NO-CODE',
    image: imageUrl,
    description: apiProduct.description || 'No description available',
    price: typeof apiProduct.price === 'number' ? apiProduct.price : 0,
    specs: Array.isArray(apiProduct.specs) ? apiProduct.specs : [],
    // Store additional data that might be needed for filtering
    ...((apiProduct.categoryIds || apiProduct.categories) && {
      categoryIds: apiProduct.categoryIds || apiProduct.categories
    })
  };
};

const ProductsSection: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortOption, setSortOption] = useState<SortOption>('default');
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [categories, setCategories] = useState<string[]>(['all']);
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>({ 'all': 'All Products' });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch products and categories from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch categories first so we can map them to products
        const categoriesResponse = await categoriesAPI.getAll();
        const categoryData = categoriesResponse.data.data || [];
        
        // Create a map of category IDs to names for display
        const categoryMap: Record<string, string> = { 'all': 'All Products' };
        const categoryIds: string[] = ['all']; // Explicitly type as string[]
        
        // Add categories from API, avoiding duplicates
        categoryData.forEach((category: any) => {
          if (category.id && category.name && !categoryIds.includes(category.id)) {
            categoryMap[category.id] = category.name;
            categoryIds.push(category.id);
          }
        });
        
        setCategories(categoryIds);
        setCategoryNames(categoryMap);
        
        // Fetch products
        const productsResponse = await productsAPI.getAll();
        const productData = productsResponse.data.data || [];
        
        console.log(productData)
        // Adapt product data to match required interface
        const adaptedProducts = productData.map(adaptProductData);
        setProducts(adaptedProducts);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Filter products based on search and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.productCode.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (selectedCategory === 'all') {
      return matchesSearch;
    }
    
    // Check if product belongs to selected category
    const productCategories = (product as any).categoryIds || [];
    return matchesSearch && productCategories.includes(selectedCategory);
    
  }).sort((a, b) => {
    switch (sortOption) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      default:
        return 0; // Maintains original order
    }
  });

  return (
    <section id="products" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-block px-3 py-1 bg-secondary/10 text-secondary rounded-full font-medium text-sm mb-4">
            Our Products
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-text">
            Browse Our Fishing Equipment
          </h2>
          <p className="max-w-2xl mx-auto text-gray-600">
            Explore our selection of handcrafted fishing stands and accessories designed for any condition.
          </p>
        </div>
        
        {/* Loading and Error States */}
        {isLoading && (
          <div className="text-center py-16">
            <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading products...</p>
          </div>
        )}
        
        {error && (
          <div className="text-center py-16 bg-red-50 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="text-lg font-heading font-bold text-red-700 mb-2">Error</h3>
            <p className="text-red-600">{error}</p>
          </div>
        )}
        
        {!isLoading && !error && (
          <>
            {/* Filter Controls */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4">
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                {categories.map(categoryId => (
                  <button
                    key={categoryId}
                    onClick={() => setSelectedCategory(categoryId)}
                    className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-standard
                      ${selectedCategory === categoryId 
                        ? 'bg-primary text-white' 
                        : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                  >
                    {categoryNames[categoryId] || categoryId}
                  </button>
                ))}
              </div>
              
              <div className="flex items-center gap-3 w-full md:w-auto">
                {/* Sort dropdown */}
                <div className="relative w-full md:w-44">
                  <select
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value as SortOption)}
                    className="w-full pl-4 pr-8 py-2 bg-white border border-gray-200 rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                  >
                    <option value="default">Sort by: Default</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                
                {/* Search input */}
                <div className="relative w-full md:w-64">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Products Grid */}
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} {...product} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-lg shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-heading font-bold text-text mb-2">No Products Found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ProductsSection; 